import { useContext, useRef } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { ensureAbsoluteUrl, PaymentMethod } from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";
import { UseFormSetValue } from "react-hook-form";

import { PaymentErrorReport, withPaypalCapturedBuyer } from "./helper";
import { HandlePostSubmit } from "./useStripePayment";

import { getAnimationNumber, TIME_BETWEEN_STEPS } from "@/canvas/features/ThankYouLoader/helpers";
import { RecaptchaAction } from "@/common/constants/recaptcha";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { navigateAfterPayment } from "@/common/helpers/thankYouPage";
import { runWithMinimumDuration } from "@/common/helpers/time";
import { AppRouterType, trpc } from "@/common/helpers/trpc";
import { useRecaptchaRetry } from "@/common/hooks/useRecaptchaRetry";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export const usePaypal = ({
  setValue,
  handlePaymentError,
  handlePostSubmit,
  setIsSubmitting,
  prepareSubmit,
}: {
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  handlePaymentError: (report: PaymentErrorReport) => void;
  handlePostSubmit: HandlePostSubmit;
  setIsSubmitting: (value: boolean) => void;
  prepareSubmit: (paymentMethod: PaymentMethod) => Promise<SubmitCommandFormInput | null>;
}) => {
  const { handleSubmitCommand } = useSubmitCommand({ setValue });
  const { commandId, formType, postTransactionUrl } = useContext(FrontendFormContext);
  const { mutateAsync: capturePaypalOrder } = trpc.paypalCaptureOrder.useMutation();
  const { mutateAsync: reOpenCommand } = trpc.reOpenCommand.useMutation();
  const { executeTrpc } = useRecaptchaRetry();

  const submitDataRef = useRef<NonNullable<SubmitCommandOutput>["data"] | null>(null);
  const paymentInputRef = useRef<DonationFormPaymentInput | TicketingPaymentInput | null>(null);

  const createPaypalOrder = async (
    paymentMethod: PaymentMethod.PayPal | PaymentMethod.Venmo
  ): Promise<string | null> => {
    const submitCommandInput = await prepareSubmit(paymentMethod);
    if (!submitCommandInput) {
      return null;
    }

    if (commandId) {
      try {
        await reOpenCommand({ commandId });
      } catch {
        datadogLogs.logger.info("paypal.reopen.skipped", { commandId, paymentMethod });
      }
    }

    datadogLogs.logger.info("paypal.submit.started", { commandId, paymentMethod });
    const submitResponse = await handleSubmitCommand(submitCommandInput);

    if (!submitResponse || isPaymentFailed(submitResponse)) {
      const errorCode =
        (submitResponse as { error: { code: string } } | undefined)?.error?.code ?? "paypal_submit_error";
      const errorMessage =
        (submitResponse as { error: { message: string } } | undefined)?.error?.message ??
        "PayPal command submission failed";
      datadogLogs.logger.error("paypal.submit.failed", { commandId, errorCode, errorMessage });
      handlePaymentError({
        rawErrorCode: errorCode,
        rawErrorMessage: errorMessage,
        step: "server-submit",
        paymentMethod,
        isExpressCheckout: false,
        commandId,
        isKnownError: true,
      });
      return null;
    }

    const typedResponse = submitResponse as unknown as { data: NonNullable<SubmitCommandOutput>["data"] };
    submitDataRef.current = typedResponse.data;
    paymentInputRef.current = submitCommandInput.paymentInput;

    const orderId = typedResponse.data?.paypalOrderId;
    if (!orderId) {
      datadogLogs.logger.error("paypal.order.failed", { commandId, errorCode: "missing_paypal_order_id" });
      handlePaymentError({
        rawErrorCode: "missing_paypal_order_id",
        rawErrorMessage: "No PayPal order ID returned from server",
        step: "server-submit",
        paymentMethod,
        isExpressCheckout: false,
        commandId,
        isKnownError: true,
      });
      return null;
    }

    datadogLogs.logger.info("paypal.order.created", { commandId, orderId });
    return orderId;
  };

  const capturePaypal = async ({ orderId }: { orderId: string }): Promise<void> => {
    datadogLogs.logger.info("paypal.capture.started", { commandId, orderId });
    setIsSubmitting(true);
    const minimumDuration = TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: false });

    const returnUrl = await runWithMinimumDuration<string | null>(async () => {
      const response = await executeTrpc(RecaptchaAction.PaypalCaptureOrder, undefined, capturePaypalOrder, {
        orderId,
        commandId: commandId || "",
      });

      if (response?.error) {
        setIsSubmitting(false);
        datadogLogs.logger.error("paypal.capture.failed", {
          commandId,
          orderId,
          errorCode: response.error.code,
          errorMessage: response.error.message,
        });
        handlePaymentError({
          rawErrorCode: response.error.code ?? "paypal_capture_error",
          rawErrorMessage: response.error.message ?? "PayPal capture failed",
          step: "server-submit",
          paymentMethod: PaymentMethod.PayPal,
          isExpressCheckout: false,
          commandId,
          isKnownError: true,
        });
        return { callbackResult: null, shouldAwaitMinimumDuration: false };
      }

      const submitData = submitDataRef.current;
      const paymentInput = paymentInputRef.current;
      if (!submitData?.command || !formType || !paymentInput || !submitData.transaction) {
        return { callbackResult: ensureAbsoluteUrl(postTransactionUrl) ?? null, shouldAwaitMinimumDuration: true };
      }

      const result = await handlePostSubmit({
        paymentInput,
        command: withPaypalCapturedBuyer(submitData.command, response?.data),
        transaction: submitData.transaction,
        formType,
        emailUpdateToken: submitData.emailUpdateToken,
        buyerTicketToken: submitData.buyerTicketToken,
      });

      datadogLogs.logger.info("paypal.capture.succeeded", { commandId, orderId });
      return {
        callbackResult: result.returnUrl ?? ensureAbsoluteUrl(postTransactionUrl) ?? null,
        shouldAwaitMinimumDuration: true,
      };
    }, minimumDuration);

    if (returnUrl) {
      await navigateAfterPayment(returnUrl);
    }
  };

  return { createPaypalOrder, capturePaypal };
};
